import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { ShipmentCard } from "@/components/shipments/ShipmentCard";
import { ShipmentFilters } from "@/components/shipments/ShipmentFilters";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowUpFromLine,
  Truck,
  RotateCcw,
  Package,
  User,
  Users,
} from "lucide-react";

interface Shipment {
  id: string;
  type: string;
  status: string;
  otpVerifiedAt?: string | null;
  slot: {
    startAt: string;
    endAt: string;
  };
  order: {
    id: string;
    publicId: string;
    status: string;
    orderType: string;
    itemCount: number;
  };
  customer: {
    publicId: string;
    name: string;
    phone?: string | null;
  };
  address: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    pincode: string;
    lat?: number | null;
    lng?: number | null;
  };
  area: {
    name: string;
  };
  assignedRider?: {
    name: string;
  } | null;
}

type ShipmentType = "PICKUP" | "DELIVERY" | "RETURN" | "REWORK";
type Scope = "my" | "unassigned";

export default function StaffShipmentsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  const [shipmentType, setShipmentType] = useState<ShipmentType>("PICKUP");
  const [scope, setScope] = useState<Scope>("my");
  const [date, setDate] = useState<Date>(new Date());
  const [status, setStatus] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: shipmentType,
        scope,
        date: format(date, "yyyy-MM-dd"),
      });

      if (status && status !== "all") {
        params.set("status", status);
      }

      if (searchQuery) {
        params.set("q", searchQuery);
      }

      const response = await fetch(`/api/staff/shipments?${params}`);
      if (response.ok) {
        const data = await response.json();
        setShipments(data.shipments || []);
      }
    } catch (error) {
      toast.error("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  }, [shipmentType, scope, date, status, searchQuery]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  async function handleClaim(shipmentId: string) {
    setClaimingId(shipmentId);
    try {
      const response = await fetch(`/api/staff/shipments/${shipmentId}/claim`, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to claim shipment");
      }

      toast.success("Shipment claimed successfully!");
      fetchShipments();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to claim");
    } finally {
      setClaimingId(null);
    }
  }

  const typeIcons: Record<ShipmentType, React.ReactNode> = {
    PICKUP: <ArrowUpFromLine className="h-4 w-4" />,
    DELIVERY: <Truck className="h-4 w-4" />,
    RETURN: <RotateCcw className="h-4 w-4" />,
    REWORK: <RotateCcw className="h-4 w-4" />,
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Shipments" />

      <Tabs
        value={shipmentType}
        onValueChange={(v) => setShipmentType(v as ShipmentType)}
      >
        <TabsList>
          <TabsTrigger value="PICKUP" className="gap-2">
            {typeIcons.PICKUP}
            Pickups
          </TabsTrigger>
          <TabsTrigger value="DELIVERY" className="gap-2">
            {typeIcons.DELIVERY}
            Deliveries
          </TabsTrigger>
          <TabsTrigger value="RETURN" className="gap-2">
            {typeIcons.RETURN}
            Returns
          </TabsTrigger>
          <TabsTrigger value="REWORK" className="gap-2">
            {typeIcons.REWORK}
            Rework
          </TabsTrigger>
        </TabsList>

        <div className="mt-4">
          <Tabs value={scope} onValueChange={(v) => setScope(v as Scope)}>
            <TabsList>
              <TabsTrigger value="my" className="gap-2">
                <User className="h-4 w-4" />
                My Shipments
              </TabsTrigger>
              <TabsTrigger value="unassigned" className="gap-2">
                <Users className="h-4 w-4" />
                Unassigned
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-4">
          <ShipmentFilters
            date={date}
            onDateChange={setDate}
            status={status}
            onStatusChange={setStatus}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearch={fetchShipments}
          />
        </div>

        <TabsContent value={shipmentType} className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-40" />
              ))}
            </div>
          ) : shipments.length > 0 ? (
            <div className="space-y-4">
              {shipments.map((shipment) => (
                <ShipmentCard
                  key={shipment.id}
                  shipment={shipment}
                  onClaim={
                    scope === "unassigned"
                      ? () => handleClaim(shipment.id)
                      : undefined
                  }
                  onOpen={() =>
                    navigate(`/staff/shipments/${shipment.id}`)
                  }
                  isClaiming={claimingId === shipment.id}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={Package}
                  title={
                    scope === "my"
                      ? "No Assigned Shipments"
                      : "No Unassigned Shipments"
                  }
                  description={
                    scope === "my"
                      ? "You don't have any shipments assigned for this date."
                      : "There are no unassigned shipments available for this date."
                  }
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
